import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import AnimatedSection from 'src/components/AnimatedSection'
import { PILLAR_NS_MAP, PillarPage } from 'src/constants/searchPages'
import { resolveOnDemandLandingUrl } from 'src/utils/onDemandFaq'

type ContentSection =
  | 'intro'
  | 'pricing'
  | 'cities'
  | 'nearshoring'
  | 'comparison_types'
  | 'on_demand'

type IProps = {
  pillar: PillarPage
  section: ContentSection
  /** Current city slug — used by the `'on_demand'` section to render a
   *  city-aware link when the slug resolves to a partner-active market. */
  currentCity?: string
}

type PricingRow = {
  size: string
  ideal: string
  price: string
}

type CityItem = {
  name: string
  slug: string
  text: string
}

function IntroSection({ pillar }: { pillar: PillarPage }) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tStats } = useTranslation('platform_stats')
  const statsInterp = { cities: tStats('CITIES') }
  const title = t('CONTENT_HUB.INTRO.TITLE', { defaultValue: '' })
  const paragraphs: string[] = t('CONTENT_HUB.INTRO.PARAGRAPHS', {
    returnObjects: true,
    defaultValue: [],
    ...statsInterp
  })

  if (!title || !paragraphs?.length) return null

  return (
    <AnimatedSection
      className="w-full bg-white-500 py-12 md:py-16"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <h2 className="max-w-2xl font-display text-2xl font-medium text-ink-700 md:text-3xl">
            {title}
          </h2>
        </div>

        <div className="mx-auto max-w-3xl columns-1 gap-8 text-center text-[15px] leading-relaxed text-ink-200 md:columns-2 md:text-left md:text-base">
          {paragraphs.map((p, i) => (
            <p key={i} className="mb-4 break-inside-avoid">
              {p}
            </p>
          ))}
        </div>
      </div>
    </AnimatedSection>
  )
}

function PricingSection({ pillar }: { pillar: PillarPage }) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tCommon } = useTranslation('common')
  const title = t('CONTENT_HUB.PRICING.TITLE', { defaultValue: '' })
  const intro = t('CONTENT_HUB.PRICING.INTRO', { defaultValue: '' })
  const table: PricingRow[] = t('CONTENT_HUB.PRICING.TABLE', {
    returnObjects: true,
    defaultValue: []
  })
  const note = t('CONTENT_HUB.PRICING.NOTE', { defaultValue: '' })

  if (!title || !table?.length) return null

  return (
    <AnimatedSection
      className="w-full bg-cream-300 py-12 md:py-16"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-10 flex flex-col items-center gap-3 text-center">
          <span className="rounded-full bg-blue-500/10 px-4 py-1 text-sm font-semibold text-blue-800">
            {tCommon('PILLAR_SECTIONS.EYEBROW_PRICES')}
          </span>
          <h2 className="max-w-2xl font-display text-2xl font-medium text-ink-700 md:text-3xl">
            {title}
          </h2>
          {intro && <p className="max-w-xl text-base text-ink-200">{intro}</p>}
        </div>

        {/* Mobile — editorial stacked rows (no horizontal scroll) */}
        <ul className="mx-auto max-w-3xl border-t border-ink-100/15 md:hidden">
          {table.map((row, i) => (
            <li
              key={i}
              className="flex flex-col gap-1 border-b border-ink-100/15 py-4"
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[15px] font-semibold text-ink-700">
                  {row.size}
                </span>
                <span className="shrink-0 text-[14px] font-semibold whitespace-nowrap text-blue-800">
                  {row.price}
                </span>
              </div>
              <p className="text-[13px] leading-relaxed text-ink-200">
                {row.ideal}
              </p>
            </li>
          ))}
        </ul>
        {/* Desktop — table */}
        <div className="mx-auto hidden max-w-3xl rounded-2xl border border-ink-100/15 bg-white-500 md:block">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-100/15 bg-cream-300">
                <th className="px-5 py-3.5 text-left text-sm font-semibold text-ink-700">
                  {tCommon('PILLAR_SECTIONS.TABLE_SIZE')}
                </th>
                <th className="px-5 py-3.5 text-left text-sm font-semibold text-ink-700">
                  {tCommon('PILLAR_SECTIONS.TABLE_IDEAL_FOR')}
                </th>
                <th className="px-5 py-3.5 text-right text-sm font-semibold text-blue-800">
                  {tCommon('PILLAR_SECTIONS.TABLE_PRICE_RANGE')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/15">
              {table.map((row, i) => (
                <tr key={i} className="transition-colors hover:bg-cream-200">
                  <td className="px-5 py-4 text-sm font-medium whitespace-nowrap text-ink-700">
                    {row.size}
                  </td>
                  <td className="px-5 py-4 text-sm text-ink-200">
                    {row.ideal}
                  </td>
                  <td className="px-5 py-4 text-right text-sm font-semibold whitespace-nowrap text-blue-800">
                    {row.price}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {note && (
          <p className="mx-auto mt-4 max-w-3xl text-center text-sm text-ink-200">
            {note}
          </p>
        )}
      </div>
    </AnimatedSection>
  )
}

function CitiesSection({ pillar }: { pillar: PillarPage }) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tCommon } = useTranslation('common')
  const title = t('CONTENT_HUB.CITIES.TITLE', { defaultValue: '' })
  const intro = t('CONTENT_HUB.CITIES.INTRO', { defaultValue: '' })
  const items: CityItem[] = t('CONTENT_HUB.CITIES.ITEMS', {
    returnObjects: true,
    defaultValue: []
  })

  if (!title || !items?.length) return null

  return (
    <AnimatedSection
      className="w-full bg-cream-300 py-12 md:py-16"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-10 flex flex-col items-center gap-3 text-center">
          <span className="rounded-full bg-blue-500/10 px-4 py-1 text-sm font-semibold text-blue-800">
            {tCommon('PILLAR_SECTIONS.EYEBROW_CITIES')}
          </span>
          <h2 className="max-w-2xl font-display text-2xl font-medium text-ink-700 md:text-3xl">
            {title}
          </h2>
          {intro && <p className="max-w-xl text-base text-ink-200">{intro}</p>}
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((city) => (
            <Link
              key={city.slug}
              href={`/${pillar}/${city.slug}`}
              className="group flex flex-col rounded-2xl border border-ink-100/15 bg-white-500 p-5 transition-colors duration-300 hover:border-blue-500/40 hover:bg-cream-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 md:p-6"
            >
              <h3 className="mb-2 text-base font-semibold text-ink-700 transition-colors group-hover:text-blue-800 md:text-lg">
                {city.name}
              </h3>
              <p className="line-clamp-3 text-sm leading-relaxed text-ink-200">
                {city.text}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </AnimatedSection>
  )
}

type ComparisonRow = {
  type: string
  size: string
  use: string
  features: string
}

function NearshoringSection({ pillar }: { pillar: PillarPage }) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const title = t('CONTENT_HUB.NEARSHORING.TITLE', { defaultValue: '' })
  const paragraphs = t('CONTENT_HUB.NEARSHORING.PARAGRAPHS', {
    returnObjects: true,
    defaultValue: []
  }) as unknown as string[]

  if (!title || !paragraphs?.length) return null

  return (
    <AnimatedSection
      className="w-full bg-white-500 py-12 md:py-16"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <span className="rounded-full bg-blue-500/10 px-4 py-1 text-sm font-semibold text-blue-800">
            Nearshoring
          </span>
          <h2 className="max-w-2xl font-display text-2xl font-medium text-ink-700 md:text-3xl">
            {title}
          </h2>
        </div>

        <div className="mx-auto max-w-3xl space-y-4 text-center text-[15px] leading-relaxed text-ink-200 md:text-left md:text-base">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </AnimatedSection>
  )
}

function ComparisonTypesSection({ pillar }: { pillar: PillarPage }) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tCommon } = useTranslation('common')
  const title = t('CONTENT_HUB.COMPARISON_TYPES.TITLE', { defaultValue: '' })
  const intro = t('CONTENT_HUB.COMPARISON_TYPES.INTRO', { defaultValue: '' })
  const table = t('CONTENT_HUB.COMPARISON_TYPES.TABLE', {
    returnObjects: true,
    defaultValue: []
  }) as unknown as ComparisonRow[]

  if (!title || !table?.length) return null

  return (
    <AnimatedSection
      className="w-full bg-cream-300 py-12 md:py-16"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-10 flex flex-col items-center gap-3 text-center">
          <h2 className="max-w-2xl font-display text-2xl font-medium text-ink-700 md:text-3xl">
            {title}
          </h2>
          {intro && <p className="max-w-xl text-base text-ink-200">{intro}</p>}
        </div>

        {/* Mobile — stacked cards (no horizontal scroll) */}
        <ul className="mx-auto max-w-4xl space-y-3 md:hidden">
          {table.map((row, i) => (
            <li
              key={i}
              className="flex flex-col gap-2 rounded-2xl border border-ink-100/15 bg-white-500 p-4"
            >
              <div className="flex items-baseline justify-between gap-4">
                <span className="text-[15px] font-semibold text-ink-700">
                  {row.type}
                </span>
                <span className="shrink-0 text-[12px] tracking-[0.06em] whitespace-nowrap text-blue-800 uppercase">
                  {row.size}
                </span>
              </div>
              <p className="text-[13px] leading-relaxed text-ink-200">
                {row.use}
              </p>
              {row.features && (
                <p className="text-[12px] leading-relaxed text-ink-200">
                  {row.features}
                </p>
              )}
            </li>
          ))}
        </ul>
        {/* Desktop — table */}
        <div className="mx-auto hidden max-w-4xl rounded-2xl border border-ink-100/15 bg-white-500 md:block">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-100/15 bg-cream-300">
                <th className="px-4 py-3.5 text-left text-sm font-semibold text-ink-700">
                  {tCommon('PILLAR_SECTIONS.TABLE_TYPE')}
                </th>
                <th className="px-4 py-3.5 text-left text-sm font-semibold text-ink-700">
                  {tCommon('PILLAR_SECTIONS.TABLE_SIZE')}
                </th>
                <th className="px-4 py-3.5 text-left text-sm font-semibold text-ink-700">
                  {tCommon('PILLAR_SECTIONS.TABLE_MAIN_USE')}
                </th>
                <th className="hidden px-4 py-3.5 text-left text-sm font-semibold text-ink-700 md:table-cell">
                  {tCommon('PILLAR_SECTIONS.TABLE_FEATURES')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/15">
              {table.map((row, i) => (
                <tr key={i} className="transition-colors hover:bg-cream-200">
                  <td className="p-4 text-sm font-medium whitespace-nowrap text-ink-700">
                    {row.type}
                  </td>
                  <td className="p-4 text-sm whitespace-nowrap text-ink-200">
                    {row.size}
                  </td>
                  <td className="p-4 text-sm text-ink-200">{row.use}</td>
                  <td className="hidden p-4 text-sm text-ink-200 md:table-cell">
                    {row.features}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AnimatedSection>
  )
}

function OnDemandSection({
  pillar,
  currentCity
}: {
  pillar: PillarPage
  currentCity?: string
}) {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const href = resolveOnDemandLandingUrl(pillar, currentCity)
  if (!href) return null

  const title = t('CONTENT_HUB_ON_DEMAND.TITLE', { defaultValue: '' })
  const description = t('CONTENT_HUB_ON_DEMAND.DESCRIPTION', {
    defaultValue: ''
  })
  const cta = t('CONTENT_HUB_ON_DEMAND.CTA', { defaultValue: '' })

  if (!title || !description || !cta) return null

  return (
    <AnimatedSection
      className="w-full bg-white-500 py-12 md:py-16"
      variant="slideUp"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="mx-auto max-w-3xl">
          <Link
            href={href}
            className="group flex flex-col gap-4 rounded-2xl border border-ink-100/15 bg-cream-300 p-6 transition-all hover:border-blue-400 hover:shadow-[0_20px_48px_-20px_rgba(18,38,63,0.25)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 md:flex-row md:items-center md:justify-between md:gap-8 md:p-8"
          >
            <div className="md:flex-1">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('CROSS_LINK_ON_DEMAND.EYEBROW', {
                  defaultValue: 'Almacenamiento a Domicilio'
                })}
              </p>
              <h3 className="mt-2 font-display text-[20px] leading-[1.2] font-medium text-ink-700 md:text-[24px]">
                {title}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                {description}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white-500 transition-colors group-hover:bg-blue-600 md:text-base">
              {cta}
            </span>
          </Link>
        </div>
      </div>
    </AnimatedSection>
  )
}

const ContentHub = ({ pillar, section, currentCity }: IProps) => {
  switch (section) {
    case 'intro':
      return <IntroSection pillar={pillar} />
    case 'pricing':
      return <PricingSection pillar={pillar} />
    case 'cities':
      return <CitiesSection pillar={pillar} />
    case 'nearshoring':
      return <NearshoringSection pillar={pillar} />
    case 'comparison_types':
      return <ComparisonTypesSection pillar={pillar} />
    case 'on_demand':
      return <OnDemandSection pillar={pillar} currentCity={currentCity} />
    default:
      return null
  }
}

export default React.memo(ContentHub)
